import { lazy, ComponentType } from 'react';

/**
 * Wraps React.lazy with automatic retry logic for transient module fetch failures (e.g. 503).
 * Retries up to `maxRetries` times with exponential backoff before giving up.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  maxRetries = 2
): React.LazyExoticComponent<T> {
  return lazy(() => retryImport(factory, maxRetries));
}

async function retryImport<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  retries: number
): Promise<{ default: T }> {
  try {
    return await factory();
  } catch (error) {
    if (retries <= 0) {
      console.error('[lazyWithRetry] All retries exhausted:', error);
      throw error;
    }
    const delay = Math.pow(2, 2 - retries) * 500; // 500ms, 1000ms
    console.warn(`[lazyWithRetry] Module load failed, retrying in ${delay}ms... (${retries} left)`);
    await new Promise(resolve => setTimeout(resolve, delay));
    return retryImport(factory, retries - 1);
  }
}
